import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ComponentType } from 'react';

import { ServiceCategory } from '../../../../models/Entities/ServiceCategory';

// own functions
import * as ownFunctions from './ServiceCategorySelector.Functions';
import { 
  Field, 
  Label, 
  Tag, 
  TagPicker, 
  TagPickerControl, 
  TagPickerGroup, 
  TagPickerInput, 
  TagPickerList, 
  TagPickerOption, 
  TagPickerProps,
  Button
} from '@fluentui/react-components';
import { Settings24Regular } from '@fluentui/react-icons';
import ServiceCategoryListComponent from '../../../../components/Services/ServiceCategories/ServiceCategory.List.Component';


const ServiceCategorySelectorComponent = (props: ownFunctions.Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!props.isLoadingSuccessfully && !props.isLoading) {
      props.GetAllServiceCategories();
    }
  }, []);
  
  const [internalCategoriesSelected, setInternalCategoriesSelected] = useState<ServiceCategory[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ServiceCategory[]>([]);
  const [isCategoryListOpen, setIsCategoryListOpen] = useState<boolean>(false);

  useEffect(() => {
    if (props.ServiceCategoriesSelected && props.ServiceCategoriesSelected.length > 0) {
      const selectedCategories: ServiceCategory[] = [];
      props.ServiceCategoriesSelected.forEach((selected: string | ServiceCategory) => {
        if (typeof selected === 'string') {
          const category = props.ServiceCategories.find((c: ServiceCategory) => c.id === selected);
          if (category) {
            selectedCategories.push(category);
          }
        } else if (typeof selected === 'object') {
          selectedCategories.push(selected as ServiceCategory);
        }
      });
      setInternalCategoriesSelected(selectedCategories);
      setSelectedOptions(selectedCategories);
    } else {
      setInternalCategoriesSelected([]);
      setSelectedOptions([]);
    }
  }, [props.ServiceCategoriesSelected, props.ServiceCategories]);

  const tagPickerOptions = props.MultiselectEnable
    ? props.ServiceCategories?.filter((category: ServiceCategory) => !internalCategoriesSelected.some(selected => selected.id === category.id))
    : props.ServiceCategories;

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (event, data) => {
      const option = props.ServiceCategories.find((c: ServiceCategory) => c.id === data.value);
      if (!option) return;
      let newSelectedOptions: ServiceCategory[] = [];
      if (props.MultiselectEnable) {
        newSelectedOptions = [...internalCategoriesSelected, option];
      } else {
        newSelectedOptions = [option];
      }
      setInternalCategoriesSelected(newSelectedOptions);
      setSelectedOptions(newSelectedOptions);
      props.onSelectedServiceCategory(newSelectedOptions);
  };

  const handleOpenCategoryList = () => {
    setIsCategoryListOpen(true);
  };

  const handleCloseCategoryList = () => {
    setIsCategoryListOpen(false);
    // Reload categories after closing
    props.GetAllServiceCategories();
  };

  return (<>
      <Field 
        label={props.showLabel !== false ? t('ServiceCategorySelector.label', 'Select a category') : undefined} 
        style={{ maxWidth: 400 }}
      >
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <TagPicker
              onOptionSelect={onOptionSelect}
              selectedOptions={internalCategoriesSelected.map(option => option.description || option.id)}
            >
              <TagPickerControl>
                <TagPickerGroup aria-label={t('ServiceCategorySelector.selectedAria', 'Selected categories')}>
                  {selectedOptions.map((option) => (
                    <Tag
                      key={option.id}
                      shape="rounded"
                      value={option.id}
                    >
                      {option.description || option.id}
                    </Tag>
                  ))}
                </TagPickerGroup>
                <TagPickerInput aria-label={t('ServiceCategorySelector.inputAria', 'Select a category')} />
              </TagPickerControl>
              <TagPickerList>
                {tagPickerOptions?.length > 0 ? (
                  tagPickerOptions?.map((option: ServiceCategory) => (
                    <TagPickerOption
                      value={option.id}
                      key={option.id}
                      text={option.description || option.id}
                    >
                      {option.description || option.id}
                    </TagPickerOption>
                  ))
                ) : (<></>)}
              </TagPickerList>
            </TagPicker>
          </div>
          {props.showManageButton && (
            <Button
              appearance="subtle"
              icon={<Settings24Regular />}
              onClick={handleOpenCategoryList}
              title={t('ServiceCategorySelector.manageCategories', 'Manage categories')}
            />
          )}
        </div>
        {props.noteText && <Label>{props.noteText}</Label>}
      </Field>

      {isCategoryListOpen && (
        <ServiceCategoryListComponent
          isOpen={isCategoryListOpen}
          onDismiss={handleCloseCategoryList}
        />
      )}
  </>);
};

export interface ServiceCategorySelectorPublicProps {
  onSelectedServiceCategory: (categories: ServiceCategory[]) => void;
  ServiceCategoriesSelected: Array<string | ServiceCategory> | undefined;
  MultiselectEnable: boolean;
  noteText?: string;
  showLabel?: boolean;
  showManageButton?: boolean;
}

const ConnectedServiceCategorySelector = connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ServiceCategorySelectorComponent as any) as ComponentType<ServiceCategorySelectorPublicProps>;

export default ConnectedServiceCategorySelector;
